import { getAuth } from 'firebase/auth';
import consola from 'consola';

/**
 * Dashboard API Service
 * Handles all dashboard-related API calls with proper authentication
 */
class DashboardService {
  constructor() {
    // Use the environment variable or fallback to the actual API URL
    // eslint-disable-next-line no-undef
    this.baseURL = import.meta.env.VITE_API_URL || "https://app.mighty45.com";
    this.auth = getAuth();

    // Cache configuration
    this.cache = new Map();
    this.cacheTimeout = 60 * 60 * 1000; // 60 minutes — aligns with React Query staleTime
    this.cacheTimes = new Map(); // Track cache timestamps
  }

  /**
   * Check if cached data is still valid
   */
  isCacheValid(key) {
    if (!this.cache.has(key) || !this.cacheTimes.has(key)) {
      return false;
    }

    const cacheTime = this.cacheTimes.get(key);
    const now = Date.now();
    return now - cacheTime < this.cacheTimeout;
  }

  /**
   * Get data from cache
   */
  getFromCache(key) {
    if (this.isCacheValid(key)) {
      consola.debug(`Dashboard cache hit for: ${key}`);
      return this.cache.get(key);
    }
    return null;
  }

  /**
   * Store data in cache
   */
  setCache(key, data) {
    consola.debug(`Dashboard cache set for: ${key}`);
    this.cache.set(key, data);
    this.cacheTimes.set(key, Date.now());
  }

  /**
   * Clear specific cache entry
   */
  clearCache(key) {
    consola.debug(`Dashboard cache cleared for: ${key}`);
    this.cache.delete(key);
    this.cacheTimes.delete(key);
  }

  /**
   * Clear all cache
   */
  clearAllCache() {
    consola.debug("Dashboard cache cleared completely");
    this.cache.clear();
    this.cacheTimes.clear();
  }

  /**
   * Generate cache key for API requests
   */
  generateCacheKey(endpoint, organizationId, params = {}) {
    const paramString =
      Object.keys(params).length > 0 ? JSON.stringify(params) : "";
    return `${endpoint}_${organizationId}_${paramString}`;
  }

  /**
   * Wait for authentication state to be ready
   */
  async waitForAuth() {
    return new Promise((resolve, reject) => {
      // If user is already available, resolve immediately
      if (this.auth.currentUser) {
        resolve(this.auth.currentUser);
        return;
      }

      // Set a timeout to avoid infinite waiting
      const timeout = setTimeout(() => {
        unsubscribe();
        reject(new Error("Authentication timeout - user not authenticated"));
      }, 5000); // 5 second timeout

      // Listen for auth state changes
      const unsubscribe = this.auth.onAuthStateChanged((user) => {
        clearTimeout(timeout);
        unsubscribe();

        if (user) {
          resolve(user);
        } else {
          reject(new Error("User not authenticated"));
        }
      });
    });
  }

  /**
   * Get authentication headers with current user token
   */
  async getAuthHeaders() {
    try {
      const user = await this.waitForAuth();
      const token = await user.getIdToken();

      return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };
    } catch (error) {
      throw new Error("User not authenticated");
    }
  }

  /**
   * Make authenticated API request with caching
   */
  async makeRequest(
    endpoint,
    options = {},
    cacheKey = null,
    skipCache = false,
  ) {
    try {
      // Check cache first (for GET requests only)
      if (
        cacheKey &&
        !skipCache &&
        (!options.method || options.method === "GET")
      ) {
        const cachedData = this.getFromCache(cacheKey);
        if (cachedData) {
          return cachedData;
        }
      }

      const headers = await this.getAuthHeaders();

      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...options,
        headers: {
          ...headers,
          ...options.headers,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        );
      }

      const data = await response.json();

      // Cache successful GET requests
      if (cacheKey && (!options.method || options.method === "GET")) {
        this.setCache(cacheKey, data);
      }

      return data;
    } catch (error) {
      console.error(`Dashboard API Error [${endpoint}]:`, error);
      throw error;
    }
  }

  /**
   * Get overview dashboard data
   * @param {string} organizationId - Organization ID
   * @param {Object} timeRange - Optional time range filter
   * @param {Date} timeRange.start - Start date
   * @param {Date} timeRange.end - End date
   * @param {boolean} forceRefresh - Skip cache and fetch fresh data
   * @param {Object} config - Optional configuration overrides
   * @param {number} config.reportQualityThreshold - Quality threshold for report compliance (default: 40)
   * @param {number} config.clientComplianceThreshold - Client compliance threshold (default: 60)
   * @param {boolean} config.useAverageCompliance - Use average vs minimum for compliance (default: true)
   * @param {number} config.workoutComplianceThreshold - Workout compliance threshold (default: 60)
   * @param {number} config.photoComplianceThreshold - Photo compliance threshold (default: 60)
   * @param {boolean} config.enableDetailedLogging - Enable detailed logging (default: false)
   */
  async getOverviewData(
    organizationId,
    timeRange = null,
    forceRefresh = false,
    config = null,
  ) {
    if (!organizationId) {
      throw new Error("Organization ID is required");
    }

    let endpoint = `/api/v1/dashboard/overview-bigquery/${organizationId}`;
    const params = {};

    // Add time range query parameters if provided
    if (timeRange) {
      const urlParams = new URLSearchParams();
      if (timeRange.start) {
        urlParams.append("start", timeRange.start.toISOString());
        params.start = timeRange.start.toISOString();
      }
      if (timeRange.end) {
        urlParams.append("end", timeRange.end.toISOString());
        params.end = timeRange.end.toISOString();
      }

      if (urlParams.toString()) {
        endpoint += `?${urlParams.toString()}`;
      }
    }

    // Prepare request options
    const requestOptions = {};

    // Add configuration as request body if provided
    if (config) {
      requestOptions.method = "POST";
      requestOptions.body = JSON.stringify(config);
      requestOptions.headers = {
        "Content-Type": "application/json",
      };
    }

    const cacheKey = this.generateCacheKey("overview", organizationId, {
      ...params,
      config,
    });
    const response = await this.makeRequest(
      endpoint,
      requestOptions,
      cacheKey,
      forceRefresh,
    );
    return response.data;
  }

  /**
   * Get client metrics data
   * @param {string} organizationId - Organization ID
   * @param {boolean} forceRefresh - Skip cache and fetch fresh data
   */
  async getClientMetrics(organizationId, forceRefresh = false) {
    if (!organizationId) {
      throw new Error("Organization ID is required");
    }

    const endpoint = `/api/v1/dashboard/clients/${organizationId}`;
    const cacheKey = this.generateCacheKey("clients", organizationId);
    const response = await this.makeRequest(
      endpoint,
      {},
      cacheKey,
      forceRefresh,
    );
    return response.data;
  }

  /**
   * Client progress over time (workouts, reports, health check-ins) for deep-dive view.
   * @param {string} organizationId
   * @param {string} clientId
   * @param {{ days?: number, forceRefresh?: boolean }} options
   */
  async getClientProgress(organizationId, clientId, options = {}) {
    if (!organizationId || !clientId) {
      throw new Error("Organization ID and client ID are required");
    }
    const days = options.days ?? 180;
    const forceRefresh = options.forceRefresh === true;
    const endpoint = `/api/v1/dashboard/client-progress/${organizationId}/${clientId}?days=${days}`;
    const cacheKey = this.generateCacheKey("client-progress", organizationId, {
      clientId,
      days,
    });
    const response = await this.makeRequest(
      endpoint,
      {},
      cacheKey,
      forceRefresh,
    );
    return response.data;
  }

  /**
   * Get activity metrics data
   * @param {string} organizationId - Organization ID
   * @param {boolean} forceRefresh - Skip cache and fetch fresh data
   */
  async getActivityMetrics(organizationId, forceRefresh = false) {
    if (!organizationId) {
      throw new Error("Organization ID is required");
    }

    const endpoint = `/api/v1/dashboard/activity/${organizationId}`;
    const cacheKey = this.generateCacheKey("activity", organizationId);
    const response = await this.makeRequest(
      endpoint,
      {},
      cacheKey,
      forceRefresh,
    );
    return response;
  }

  /**
   * Get communication metrics data
   * @param {string} organizationId - Organization ID
   * @param {boolean} forceRefresh - Skip cache and fetch fresh data
   */
  async getCommunicationMetrics(organizationId, forceRefresh = false) {
    if (!organizationId) {
      throw new Error("Organization ID is required");
    }

    const endpoint = `/api/v1/dashboard/communication/${organizationId}`;
    const cacheKey = this.generateCacheKey("communication", organizationId);
    const response = await this.makeRequest(
      endpoint,
      {},
      cacheKey,
      forceRefresh,
    );
    return response.data;
  }

  /**
   * Get communication summary data with insights
   * @param {string} organizationId - Organization ID
   * @param {boolean} forceRefresh - Skip cache and fetch fresh data
   */
  async getCommunicationSummary(organizationId, forceRefresh = false) {
    if (!organizationId) {
      throw new Error("Organization ID is required");
    }

    const endpoint = `/api/v1/dashboard/communication-summary/${organizationId}`;
    const cacheKey = this.generateCacheKey(
      "communication-summary",
      organizationId,
    );
    const response = await this.makeRequest(
      endpoint,
      {},
      cacheKey,
      forceRefresh,
    );
    return response;
  }

  /**
   * Get business metrics data
   * @param {string} organizationId - Organization ID
   * @param {boolean} forceRefresh - Skip cache and fetch fresh data
   */
  async getBusinessMetrics(organizationId, forceRefresh = false) {
    if (!organizationId) {
      throw new Error("Organization ID is required");
    }

    const endpoint = `/api/v1/dashboard/business/${organizationId}`;
    const cacheKey = this.generateCacheKey("business", organizationId);
    const response = await this.makeRequest(
      endpoint,
      {},
      cacheKey,
      forceRefresh,
    );
    return response.data;
  }

  /**
   * Get AI insights (separate endpoint - completely independent)
   * @param {string} startDate - Start date (YYYY-MM-DD format)
   * @param {string} endDate - End date (YYYY-MM-DD format)
   * @param {boolean} forceRefresh - Skip cache and fetch fresh data
   */
  async getAIInsights(startDate, endDate, forceRefresh = false) {
    const urlParams = new URLSearchParams();
    if (startDate) urlParams.append("startDate", startDate);
    if (endDate) urlParams.append("endDate", endDate);

    const endpoint = `/api/v1/dashboard/ai-insights?${urlParams.toString()}`;
    const cacheKey = this.generateCacheKey("ai-insights", "user", {
      startDate,
      endDate,
    });

    const response = await this.makeRequest(
      endpoint,
      {},
      cacheKey,
      forceRefresh,
    );
    return response.data;
  }

  /**
   * Generate AI content
   * @param {string} organizationId - Organization ID
   * @param {Object} request - Content generation request
   */
  async generateAIContent(organizationId, request) {
    if (!organizationId) {
      throw new Error("Organization ID is required");
    }

    // Don't cache POST requests for AI content generation
    const response = await this.makeRequest(
      `/api/v1/dashboard/ai/${organizationId}/generate-content`,
      {
        method: "POST",
        body: JSON.stringify(request),
      },
    );
    return response.data;
  }

  /**
   * Refresh dashboard cache
   * @param {string} organizationId - Organization ID
   * @param {string} type - Cache type to refresh (optional)
   */
  async refreshCache(organizationId, type = null) {
    if (!organizationId) {
      throw new Error("Organization ID is required");
    }

    // Clear local cache for this organization
    if (type) {
      // Clear specific cache type
      const cacheKey = this.generateCacheKey(type, organizationId);
      this.clearCache(cacheKey);
    } else {
      // Clear all cache for this organization
      const keysToDelete = [];
      for (const key of this.cache.keys()) {
        if (key.includes(`_${organizationId}_`)) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach((key) => this.clearCache(key));
    }

    // Call server-side cache refresh
    let endpoint = `/api/v1/dashboard/cache/refresh/${organizationId}`;
    if (type) {
      endpoint += `?type=${type}`;
    }

    const response = await this.makeRequest(
      endpoint,
      { method: "POST" },
      null,
      true,
    );
    return response.data;
  }

  /**
   * Preload dashboard data for better performance
   * @param {string} organizationId - Organization ID
   * @param {Object} timeRange - Optional time range filter
   */
  async preloadDashboardData(organizationId, timeRange = null) {
    if (!organizationId) {
      throw new Error("Organization ID is required");
    }

    try {
      // Preload all dashboard data in parallel
      const startDate = timeRange?.start?.toISOString().split("T")[0];
      const endDate = timeRange?.end?.toISOString().split("T")[0];

      const promises = [
        this.getOverviewData(organizationId, timeRange),
        this.getActivityMetrics(organizationId),
        this.getCommunicationMetrics(organizationId),
        this.getBusinessMetrics(organizationId),
        this.getClientMetrics(organizationId),
      ];

      await Promise.allSettled(promises);
      consola.success("Dashboard data preloaded successfully");
    } catch (error) {
      console.warn("Some dashboard data failed to preload:", error);
    }
  }
}

// Create singleton instance
const dashboardService = new DashboardService();

export default dashboardService;

/**
 * Hook for using dashboard service in React components
 */
export const useDashboardService = () => {
  return dashboardService;
};

/**
 * Hook for dashboard caching utilities
 */
export const useDashboardCache = () => {
  return {
    clearCache: (key) => dashboardService.clearCache(key),
    clearAllCache: () => dashboardService.clearAllCache(),
    refreshCache: (organizationId, type) => dashboardService.refreshCache(organizationId, type),
    preloadData: (organizationId, timeRange) => dashboardService.preloadDashboardData(organizationId, timeRange),
    isCacheValid: (key) => dashboardService.isCacheValid(key),
    getCacheStats: () => ({
      size: dashboardService.cache.size,
      keys: Array.from(dashboardService.cache.keys())
    })
  };
};

/**
 * Error types for better error handling
 */
export const DashboardErrorTypes = {
  AUTHENTICATION_ERROR: 'AUTHENTICATION_ERROR',
  AUTHORIZATION_ERROR: 'AUTHORIZATION_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  DATA_ERROR: 'DATA_ERROR',
  CACHE_ERROR: 'CACHE_ERROR'
};

/**
 * Cache configuration constants
 */
export const CacheConfig = {
  DEFAULT_TIMEOUT: 5 * 60 * 1000, // 5 minutes
  LONG_CACHE_TIMEOUT: 15 * 60 * 1000, // 15 minutes for stable data
  SHORT_CACHE_TIMEOUT: 2 * 60 * 1000, // 2 minutes for frequently changing data
  MAX_CACHE_SIZE: 100 // Maximum number of cache entries
};

/**
 * Utility function to classify dashboard errors
 */
export const classifyDashboardError = (error) => {
  const message = error.message.toLowerCase();
  
  if (message.includes('authentication') || message.includes('token')) {
    return DashboardErrorTypes.AUTHENTICATION_ERROR;
  }
  
  if (message.includes('authorization') || message.includes('access denied')) {
    return DashboardErrorTypes.AUTHORIZATION_ERROR;
  }
  
  if (message.includes('network') || message.includes('fetch')) {
    return DashboardErrorTypes.NETWORK_ERROR;
  }
  
  if (message.includes('cache')) {
    return DashboardErrorTypes.CACHE_ERROR;
  }
  
  return DashboardErrorTypes.DATA_ERROR;
};
